import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { useOverlayFocusTrap } from '../utils/useOverlayFocusTrap.js';
import { isTopOverlay, popOverlay, pushOverlay } from '../utils/overlayEscStack.js';
import { usePresence } from '../utils/usePresence.js';
import { overlayPresenceProps } from '../hooks/mistRail.js';
import { IconButton } from './IconButton.js';
import styles from './Drawer.module.css';

/** Enter spring.slow (280ms); exit spring.slow-exit (180ms) — fallback ≥ enter. */
const DRAWER_MS = 280;

export type DrawerProps = {
  opened: boolean;
  onClose: () => void;
  /**
   * When true, Esc / backdrop / X call `onCloseGuard` instead of `onClose`
   * (e.g. confirm before discarding a dirty wizard).
   */
  closeGuard?: boolean;
  /** Invoked instead of `onClose` while `closeGuard` is true. */
  onCloseGuard?: () => void;
  /** Fires after close transition finishes (panel unmounted). */
  onExited?: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** `top` / `bottom` = sheet from edge (full width). `left` / `right` = side panels. */
  placement?: 'right' | 'left' | 'top' | 'bottom';
  /** Extra class on drawer panel (aside). */
  className?: string;
  /** Extra class on drawer header strip. */
  headerClassName?: string;
  /** Compact header row (wizard sheets with fullscreen toggle). */
  headerDense?: boolean;
  /** Extra class on title element. */
  titleClassName?: string;
  /** Extra class on close IconButton. */
  closeClassName?: string;
  /** Extra class on drawer body (scrollable content area). */
  bodyClassName?: string;
  /** Slot before close control (e.g. maximize / restore). */
  headerActions?: ReactNode;
  /** Full-bleed strip under header row (e.g. wizard progress as bottom edge). */
  headerBottom?: ReactNode;
  /** Sticky chrome under body (mirrors header: mist fill + grain). */
  footer?: ReactNode;
  /** Extra class on drawer footer strip. */
  footerClassName?: string;
  /** Skip built-in header (caller owns title/close chrome). */
  hideHeader?: boolean;
  /** Dialog name when `hideHeader` (or no title). */
  'aria-label'?: string;
};

export function Drawer({
  opened,
  onClose,
  closeGuard = false,
  onCloseGuard,
  onExited,
  title,
  children,
  placement = 'right',
  className,
  headerClassName,
  headerDense = false,
  titleClassName,
  closeClassName,
  bodyClassName,
  headerActions,
  headerBottom,
  footer,
  footerClassName,
  hideHeader = false,
  'aria-label': ariaLabel,
}: DrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  const { mounted, visible } = usePresence(opened, { ms: DRAWER_MS, panelRef, onExited });

  useOverlayFocusTrap(mounted && visible, panelRef);

  const requestClose = useCallback(() => {
    if (closeGuard && onCloseGuard) {
      onCloseGuard();
      return;
    }
    onClose();
  }, [closeGuard, onCloseGuard, onClose]);

  useEffect(() => {
    if (!mounted || !visible) return;
    const stackId = pushOverlay();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Nested overlays: only the top-most one closes on Escape.
      if (!isTopOverlay(stackId)) return;
      e.preventDefault();
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      popOverlay(stackId);
    };
  }, [mounted, visible, requestClose]);

  if (!mounted) return null;

  return createPortal(
    <div className={styles.portalHost} data-wp-drawer-portal="">
      <div
        className={cx(styles.backdrop, visible && styles.backdropVisible)}
        aria-hidden
        onMouseDown={(e) => e.target === e.currentTarget && requestClose()}
      />
      <aside
        ref={panelRef}
        className={cx(
          styles.panel,
          placement === 'top'
            ? styles.panelTop
            : placement === 'bottom'
              ? styles.panelBottom
              : placement === 'left'
                ? styles.panelLeft
                : styles.panelRight,
          visible && styles.panelVisible,
          className,
        )}
        aria-labelledby={title && !hideHeader ? titleId : undefined}
        aria-label={title && !hideHeader ? undefined : (ariaLabel ?? 'Panneau')}
        role="dialog"
        aria-modal="true"
        {...overlayPresenceProps(visible)}
      >
        {!hideHeader ? (
          <div
            className={cx(
              styles.header,
              !title && styles.headerOnlyClose,
              headerBottom != null && styles.headerWithBottom,
              headerDense && styles.headerDense,
              headerClassName,
            )}
          >
            <div className={styles.headerMain}>
              {title ? (
                <h2 id={titleId} className={cx(styles.title, titleClassName)}>
                  {title}
                </h2>
              ) : null}
              {headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null}
              <IconButton
                type="button"
                variant="ghost"
                size="md"
                className={cx(styles.close, closeClassName)}
                aria-label="Fermer"
                onClick={requestClose}
              >
                <IconX size={22} stroke={1.5} aria-hidden />
              </IconButton>
            </div>
            {headerBottom != null ? <div className={styles.headerBottom}>{headerBottom}</div> : null}
          </div>
        ) : null}
        <div
          className={cx(
            styles.body,
            styles.bodyInset,
            footer != null && styles.bodyWithFooter,
            bodyClassName,
          )}
          data-wp-mist-scroll=""
        >
          {children}
        </div>
        {footer != null ? (
          <div className={cx(styles.footer, footerClassName)}>{footer}</div>
        ) : null}
      </aside>
    </div>,
    document.body,
  );
}
